import { createSelector } from 'reselect';
import isBoolean from 'lodash/isBoolean';
import commonHelper from '@das/canvas-common/src/helper';

import contractMapSelector from 'app/App/selectors/contractMap';
import componentSelector from 'app/Canvas/selectors/component';
import {
  DEFAULT_CONTENT_CONTEXT,
  DEVICE,
  DISPLAY_ON_APP_ANDROID,
  DISPLAY_ON_APP_IOS,
  DISPLAY_ON_DESKTOP,
  DISPLAY_ON_MOBILE,
  DISPLAY_ON_EMAIL,
  GEOLOCATION_LOCAL_MARKET,
  GEOLOCATION_STATE
} from 'app/ContentContext/const';
import { experimentsFilter, getExperimentsAndVariants } from 'app/Experiments/utils';
import { getLocalPreviewerName } from 'app/LocalCanvasPreview/utils';
import {
  metadataFieldsSelector,
  pageIdSelector,
  pageNameSelector,
  pagePayloadSelector,
  pageTemplateNameSelector,
  pageTypeSelector
} from 'app/Page/selectors';
import { PAGE_TYPES } from 'app/RenderingEnvironment/const';
import { APP_TRANSFORM_CONFIG, transform } from 'lib/atomic/atomic-utils';
import templateMapper from 'utils/templateMapper/templateMapper';

import { populateSbnPreviewProps } from './previewContentHelper';
import { contentContextFilter } from './contentContextHelper';
import { PREVIEW_ENV, EMPTY_COMPONENT_PAYLOAD } from './const';

// preview state data is kept in canvas.context
export const contextSelector = state => state?.canvas?.context ?? {};

// all hidden experiment state
export const hiddenExperimentsSelector = createSelector(
  contextSelector,
  context => context.hiddenExperiments || {}
);

// all content context preview state
export const contentContextStatesSelector = createSelector(
  contextSelector,
  context => context.preview || {}
);
export const deviceSelector = createSelector(contextSelector, context => context.device || '');
export const isMobileSelector = createSelector(contextSelector, context => {
  if (isBoolean(context.isMobile)) {
    return context.isMobile;
  }
  return false;
});

// other preview state selectors
export const componentHoveringSelector = createSelector(
  contextSelector,
  context => context.componentHovering || EMPTY_COMPONENT_PAYLOAD
);
export const componentSelectedSelector = createSelector(
  contextSelector,
  context => context.componentSelected || EMPTY_COMPONENT_PAYLOAD
);
export const contentLinkDisabledSelector = createSelector(contextSelector, context => {
  if (isBoolean(context.contentLinkDisabled)) {
    return context.contentLinkDisabled;
  }
  return true;
});
export const mwpIFrameSelector = createSelector(
  contextSelector,
  context => context.mwpiFrameRef || {}
);

// all experiment names and variants on page payload
export const allExperimentVariantsSelector = createSelector(pagePayloadSelector, pagePayload =>
  getExperimentsAndVariants(pagePayload?.Payload?.Components)
);

// content after experiment and content context filters
export const filterPreviewContentSelector = createSelector(
  pagePayloadSelector,
  contractMapSelector,
  hiddenExperimentsSelector,
  isMobileSelector,
  contentContextStatesSelector,
  (pagePayload, contractMap, hiddenExperiments, isMobile, contentContextStates) => {
    // keep track of component ids that are hidden
    const hiddenIds = [];

    // create array of flattened components
    const { Components: components = [] } = commonHelper.createCapiResponse(pagePayload);

    // remove experiment containers and child variants that match listed in `hiddenExperiments`
    let { hide, keep } = experimentsFilter({ components, hiddenExperiments });
    hiddenIds.push(...hide);

    // content context filtering
    const getContract = schemaName => contractMap[schemaName];

    // apply filters for each content context
    Object.keys(DEFAULT_CONTENT_CONTEXT).forEach(key => {
      let values = contentContextStates?.[key] ?? [];
      if (key === DEVICE) {
        // for sake of preview, always display app components
        values = [DISPLAY_ON_APP_ANDROID, DISPLAY_ON_APP_IOS, DISPLAY_ON_EMAIL].concat(
          isMobile ? DISPLAY_ON_MOBILE : DISPLAY_ON_DESKTOP
        );
      }

      // Since we donot have GEOLOCATION_LOCAL_MARKET (or) GEOLOCATION_STATE in Render For popover,
      // do not filter components based on those two properties (also causes a bug w/ rendering preview. eg: Render For US)
      if (key !== GEOLOCATION_LOCAL_MARKET && key !== GEOLOCATION_STATE) {
        ({ hide, keep } = contentContextFilter({
          getContract,
          components: keep,
          field: key,
          filterValues: values
        }));
      }
      hiddenIds.push(...hide);
    });

    return {
      filteredComponents: keep,
      hiddenIds
    };
  }
);

// flattened (dcapi-like) page payload with filtered content
export const previewContentSelector = createSelector(
  metadataFieldsSelector,
  pageIdSelector,
  pageNameSelector,
  pageTemplateNameSelector,
  pageTypeSelector,
  isMobileSelector,
  filterPreviewContentSelector,
  (
    metadataFields,
    pageId,
    pageName,
    pageTemplateName,
    pageType,
    isMobile,
    { filteredComponents }
  ) => {
    // do stuff to filteredComponents based on pageType
    let previewComponents;
    switch (pageType) {
      case PAGE_TYPES.APP:
        previewComponents = filteredComponents.map(payload =>
          transform({ config: APP_TRANSFORM_CONFIG, payload })
        );
        break;
      case PAGE_TYPES.PERSONALIZED_HOMEPAGE:
        previewComponents = templateMapper(filteredComponents);
        break;
      case PAGE_TYPES.HOMEPAGE:
        previewComponents = filteredComponents;
        break;
      default:
        previewComponents = filteredComponents;
    }

    // component preview does not have a page id
    const isPreviewingComponent = !pageId;

    // For SBN pages (content and product result pages), add props required for MWP preview
    if (
      (pageType === PAGE_TYPES.RESPONSIVE_CONTENT ||
        pageType === PAGE_TYPES.DEFAULT ||
        pageType === PAGE_TYPES.CATEGORY_NAVIGATION_PRODUCT_RESULTS ||
        pageType === PAGE_TYPES.FLASH_EVENT) &&
      !isPreviewingComponent
    ) {
      const sbnPreviewProps = populateSbnPreviewProps(previewComponents);
      return {
        ...sbnPreviewProps,
        MetadataFields: metadataFields,
        PageTemplateName: pageTemplateName
      };
    }

    return {
      Components: previewComponents,
      Id: pageId,
      MetadataFields: metadataFields,
      PageName: pageName,
      PageTemplateName: pageTemplateName
    };
  }
);

// metadata used for preview logic
export const previewMetadataSelector = createSelector(
  componentSelector,
  contentContextStatesSelector,
  isMobileSelector,
  pageIdSelector,
  pageNameSelector,
  pageTemplateNameSelector,
  pageTypeSelector,
  (
    componentDetails,
    contentContextStates,
    isMobile,
    pageId,
    pageName,
    pageTemplateName,
    pageType
  ) => {
    // frame is only enabled when there is enough preview data
    const frameEnabled = [pageName, pageTemplateName, pageType].every(Boolean);

    // component preview does not have a page id
    const isPreviewingComponent = !pageId;

    // enable external mwp preview for:
    // 1. desktop only because we are currently unable to get mobile platform env
    // 2. page preview because component preview requires custom canvas component map
    // 3. a list of page types under mwpPreviewSupportedPages
    const mwpPreviewSupportedPages = [
      PAGE_TYPES.HOMEPAGE,
      PAGE_TYPES.SPACE,
      PAGE_TYPES.PDP,
      PAGE_TYPES.DEFAULT,
      PAGE_TYPES.CATEGORY_NAVIGATION_PRODUCT_RESULTS,
      PAGE_TYPES.RESPONSIVE_CONTENT,
      PAGE_TYPES.FLASH_EVENT
    ];
    const isPreviewingOnMWP =
      !isPreviewingComponent && mwpPreviewSupportedPages.some(type => pageType === type);

    // TODO figure out to tell when previewable content is static
    // `previewContentSelector` returns a DCAPI-like payload so `IsStaticContent` is lost
    const { IsStaticContent: isStatic } = componentDetails;

    // local preview component to load
    const localPreviewerName = getLocalPreviewerName({ isStatic, pageType });

    // src to use for preview
    let previewSrc;

    switch (pageType) {
      case PAGE_TYPES.PDP: {
        previewSrc = `${PREVIEW_ENV}/s/a-tint-of-glam-hydrating-extra-lip-tint-duo-70-value/7396978?canvasPreview=true`;
        break;
      }
      case PAGE_TYPES.RESPONSIVE_CONTENT:
      case PAGE_TYPES.FLASH_EVENT: {
        previewSrc = `${PREVIEW_ENV}/browse/women?canvasPreview=true`;
        break;
      }
      case PAGE_TYPES.DEFAULT:
      case PAGE_TYPES.CATEGORY_NAVIGATION_PRODUCT_RESULTS: {
        previewSrc = `${PREVIEW_ENV}/browse/men/clothing?canvasPreview=true`;
        break;
      }
      case PAGE_TYPES.HOMEPAGE:
      case PAGE_TYPES.SPACE:
      default:
        previewSrc = `${PREVIEW_ENV}/?canvasPreview=true`;
    }

    const mobileQueryParams = isMobile ? '&responsiveStrategy=mobile' : '';

    previewSrc += mobileQueryParams.concat('&experimentId=Default');

    return {
      frameEnabled,
      isMobile,
      isPreviewingComponent,
      isPreviewingOnMWP,
      localPreviewerName,
      previewSrc
    };
  }
);

// ids that are hidden due to filtering
export const unsupportedComponentsSelector = createSelector(
  filterPreviewContentSelector,
  ({ hiddenIds }) => hiddenIds || []
);
